import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { QRCodeCanvas } from "qrcode.react";
import { Link } from "react-router-dom";
import { Plus, LogOut, QrCode, X, Lock, Camera, Check, Edit2 } from "lucide-react";

export default function Profile() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [showQR, setShowQR] = useState(false);
  
  // States for Profile Customization
  const [isEditingName, setIsEditingName] = useState(false);
  const [uploading, setUploading] = useState(false);

  const faculties = [
    "Rotterdam School of Management", "Erasmus School of Economics", "Erasmus School of Law",
    "Erasmus School of Social and Behavioural Sciences", "Erasmus Medical Center",
    "Erasmus School of History, Culture and Communication", "Erasmus School of Philosophy",
    "Erasmus School of Health Policy and Management",
  ];

  const situations = ["BSc 1", "BSc 2", "BSc 3", "Pre-Master", "Master","Staff"];
  const validUntil = `Dec ${new Date().getFullYear()}`;

  // 1. Initial Load & Auto-Creation Logic
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const sessionUser = sessionData.session?.user ?? null;
        
        if (sessionUser) {
          setUser(sessionUser);

          // Try to fetch profile
          let { data: existingProfile, error: fetchError } = await supabase
            .from("profiles")
            .select("*")
            .eq("user_id", sessionUser.id)
            .single();

          // If profile doesn't exist, create it immediately
          if (fetchError && (fetchError.code === "PGRST116" || fetchError.code === "406")) {
            const newProfile = {
              user_id: sessionUser.id,
              email: sessionUser.email,
              full_name: sessionUser.user_metadata?.full_name || "New Member",
              avatar_url: sessionUser.user_metadata?.avatar_url || null,
              faculty: "",
              situation: "",
              points: 0,
              has_card: false,
            };

            const { data: createdProfile, error: insertError } = await supabase
              .from("profiles")
              .insert([newProfile])
              .select()
              .single();

            if (!insertError) {
              existingProfile = createdProfile;
            } else {
              console.error("Profile Creation Error:", insertError.message);
            }
          }

          if (existingProfile) {
            setProfile(existingProfile);
          }

          // Fetch Transactions
          const { data: transData } = await supabase
            .from("transactions")
            .select(`id, created_at, points_awarded, note, partners ( logo_url )`)
            .eq("user_id", sessionUser.id)
            .order("created_at", { ascending: false })
            .limit(3);

          if (transData) setTransactions(transData);
        }
      } catch (err) {
        console.error("General Setup Error:", err);
      } finally {
        setLoading(false);
      }
    };
    loadInitialData();
  }, []); // Run once on mount

  const logout = async () => {
    await supabase.auth.signOut();
    window.location.reload();
  };

  // 2. Optimized Profile Update (Syncs to DB)
  const handleProfileChange = async (field, value) => {
    if (!user) return;
    
    // Update local state immediately for UI responsiveness
    setProfile(prev => ({ ...prev, [field]: value }));

    const { error } = await supabase
      .from("profiles")
      .update({ [field]: value })
      .eq("user_id", user.id);

    if (error) {
      console.error("Update Error:", error.message);
    }
  };

  // 3. Image Upload Logic (Matches your UID folder policy)
  const uploadAvatar = async (event) => {
    try {
      setUploading(true);
      if (!event.target.files || event.target.files.length === 0) return;
      
      const file = event.target.files[0];
      const fileExt = file.name.split('.').pop();
      const filePath = `${user.id}/${Math.random()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      await handleProfileChange("avatar_url", publicUrl);
    } catch (error) {
      alert('Upload failed: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const styles = {
    container: { minHeight: "100vh", backgroundColor: "#FFFFFF", padding: "2rem 1.5rem 100px 1.5rem", fontFamily: "'Inter', sans-serif", color: "#2D302D" },
    statusCard: { backgroundColor: "#D1EAD9", borderRadius: "32px", padding: "2rem 1.5rem", marginBottom: "2rem" },
    progressBar: { width: `${Math.min(profile?.points || 0, 100)}%`, height: "100%", background: "linear-gradient(90deg, #12873B 0%, #2D302D 100%)", borderRadius: "20px", transition: "width 1s ease" },
    selectField: { width: "100%", padding: "1rem", borderRadius: "16px", border: "1.5px solid #2D302D", backgroundColor: "#FFFFFF", fontSize: "0.95rem", fontWeight: "600", marginBottom: "1.5rem", textAlign: "center" },
    btnGradient: { width: "100%", padding: "1.2rem", borderRadius: "22px", border: "none", background: "linear-gradient(90deg, #12873B 0%, #2D302D 100%)", color: "white", fontWeight: "800", fontSize: "1.1rem", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" },
    qrButton: { width: "100%", padding: "0.8rem", borderRadius: "16px", border: "1.5px solid #D1EAD9", background: "#F8FAFB", color: "#12873B", fontWeight: "700", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", marginBottom: "2rem" },
    avatarWrapper: { position: "relative", width: "70px", height: "70px" },
    uploadOverlay: { position: "absolute", bottom: "-2px", right: "-2px", backgroundColor: "#12873B", borderRadius: "50%", padding: "6px", border: "2px solid #D1EAD9", cursor: "pointer", display: "flex" }
  };

  if (loading) return null;

  return (
    <div style={styles.container}>
      {/* Header with Customization */}
      <div style={styles.statusCard}>
        <div style={{ display: "flex", alignItems: "center", gap: "1.2rem", marginBottom: "1.5rem" }}>
          
          {/* Avatar Area */}
          <div style={styles.avatarWrapper}>
            <img 
              src={profile?.avatar_url || "https://via.placeholder.com/70"} 
              alt="User" 
              style={{ width: "70px", height: "70px", borderRadius: "50%", border: "3px solid #FFF", objectFit: "cover", opacity: uploading ? 0.5 : 1 }} 
            />
            <label htmlFor="avatar-upload" style={styles.uploadOverlay}>
              <Camera size={14} color="white" />
              <input id="avatar-upload" type="file" accept="image/*" onChange={uploadAvatar} disabled={uploading} style={{ display: "none" }} />
            </label>
          </div>

          <div style={{ flex: 1 }}>
            {/* Inline Name Editing */}
            {isEditingName ? (
              <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                <input
                  value={profile?.full_name || ""}
                  autoFocus
                  onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
                  onBlur={() => {
                    handleProfileChange("full_name", profile.full_name);
                    setIsEditingName(false);
                  }}
                  style={{ fontSize: "1.2rem", fontWeight: "800", color: "#12873B", border: "none", borderBottom: "2px solid #12873B", outline: "none", background: "transparent", width: "100%" }}
                />
                <Check size={20} color="#12873B" onClick={() => setIsEditingName(false)} />
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }} onClick={() => setIsEditingName(true)}>
                <h2 style={{ fontSize: "1.3rem", fontWeight: "800", color: "#12873B", margin: 0 }}>{profile?.full_name || "New Member"}</h2>
                <Edit2 size={14} color="#12873B" style={{ opacity: 0.5 }} />
              </div>
            )}
            <p style={{ fontSize: "0.85rem", opacity: 0.7, margin: 0 }}>Valid until: {validUntil}</p>
          </div>
        </div>

        <div style={{ textAlign: "right", fontWeight: "900", color: "#12873B" }}>{profile?.points || 0} Points</div>
        <div style={{ width: "100%", height: "14px", backgroundColor: "#FFF", borderRadius: "20px", marginTop: "8px", overflow: "hidden" }}>
          <div style={styles.progressBar}></div>
        </div>
      </div>

      {/* Dropdowns */}
      <div style={{ textAlign: "center" }}>
        <span style={{ display: "block", color: "#12873B", fontWeight: "700", fontSize: "0.8rem", textTransform: "uppercase", marginBottom: "8px" }}>My Faculty</span>
        <select style={styles.selectField} value={profile?.faculty || ""} onChange={(e) => handleProfileChange("faculty", e.target.value)}>
          <option value="">Select Faculty</option>
          {faculties.map(f => <option key={f} value={f}>{f}</option>)}
        </select>

        <span style={{ display: "block", color: "#12873B", fontWeight: "700", fontSize: "0.8rem", textTransform: "uppercase", marginBottom: "8px" }}>Academic Year</span>
        <select style={styles.selectField} value={profile?.situation || ""} onChange={(e) => handleProfileChange("situation", e.target.value)}>
          <option value="">Select Situation</option>
          {situations.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* QR Section */}
      {profile?.has_card ? (
        <>
          {!showQR ? (
            <button style={styles.qrButton} onClick={() => setShowQR(true)}>
              <QrCode size={20} /> Show QR Code
            </button>
          ) : (
            <div style={{ textAlign: "center", background: "#F1F1F1", padding: "20px", borderRadius: "24px", marginBottom: "2rem", position: "relative" }}>
              <button onClick={() => setShowQR(false)} style={{ position: "absolute", top: "10px", right: "10px", border: "none", background: "none" }}>
                <X size={20} color="#94A3B8" />
              </button>
              <QRCodeCanvas value={`${window.location.origin}/seller?user_id=${user?.id}`} size={140} bgColor="#F1F1F1" fgColor="#12873B" level="H" />
              <p style={{ fontSize: "0.8rem", fontWeight: "700", color: "#12873B", marginTop: "10px" }}>Scan at checkout</p>
            </div>
          )}
        </>
      ) : (
        <div style={{ padding: "1.5rem", backgroundColor: "#F8FAFB", borderRadius: "24px", textAlign: "center", border: "1.5px dashed #D1EAD9", marginBottom: "2rem" }}>
          <Lock size={24} color="#94A3B8" />
          <p style={{ fontSize: "0.9rem", fontWeight: "700", margin: "8px 0" }}>Digital ID Locked</p>
          <Link to="/get-card" style={{ color: "#12873B", fontWeight: "800", textDecoration: "none" }}>GET YOUR CARD!</Link>
        </div>
      )}

      {/* Recent Activity */}
      <h3 style={{ fontSize: "1.1rem", fontWeight: "800", color: "#12873B", marginBottom: "1rem" }}>Recent Activity</h3>
      {transactions.length > 0 ? transactions.map((t) => (
        <div key={t.id} style={{ display: "flex", alignItems: "center", gap: "1rem", padding: "1rem", backgroundColor: "#F8FAFB", borderRadius: "20px", marginBottom: "10px" }}>
          <img src={t.partners?.logo_url || "https://via.placeholder.com/32"} alt="store" style={{ width: "40px", height: "40px", borderRadius: "12px", background: "white", objectFit: "cover" }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: "700", fontSize: "0.95rem" }}>{t.note || "Point Reward"}</div>
            <div style={{ fontSize: "0.75rem", color: "#94A3B8" }}>{new Date(t.created_at).toLocaleDateString()}</div>
          </div>
          <div style={{ fontWeight: "800", color: "#12873B" }}>+{t.points_awarded}</div>
        </div>
      )) : <p style={{ textAlign: "center", color: "#94A3B8", fontSize: "0.9rem" }}>No recent activity yet.</p>}

      {/* Footer Buttons */}
      <div style={{ marginTop: "2rem", display: "flex", flexDirection: "column", gap: "12px" }}>
        <Link to="/partners" style={{ textDecoration: "none" }}>
          <button style={styles.btnGradient}><Plus size={20} /> Get more points!</button>
        </Link>
        <button onClick={logout} style={{ background: "none", border: "none", color: "#94A3B8", fontWeight: "700", cursor: "pointer", padding: "10px" }}>
          <LogOut size={18} /> Logout
        </button>
      </div>
    </div>
  );
}